// Pre-defined failure reasons per component, sourced from the FD Check Ltd defects template.
// When an inspector answers "No" to a checklist question, the matching set of reasons
// is presented as a multi-select checklist plus an "Other (free text)" field.

export type FailureReasonGroup = {
  key: string;
  label: string;
  reasons: string[];
};

// Iter-109g — Advisory reasons. When the ONLY selected failure reason
// for a component is in this set, the inspection is not marked as a
// FAIL because of that item — instead it's an "advisory" line item.
// The quote engine still lists the advice, but no remedial line is
// auto-added. This mirrors the surveyor guidance for "solid 44 mm
// notional / nominal fire doors" where an upgrade/adjustment MAY be
// required but a straight replacement is not warranted.
export const ADVISORY_REASONS: Set<string> = new Set<string>([
  'Certification could not be confirmed; however, as a solid 44 mm door, under previous regulations this may have been deemed a notional or nominal fire door — adjustment or upgrade only may be required. Further consultation may assist.',
]);

/**
 * Returns true when this exact reason string is registered as an
 * "advisory" (not a hard failure). Matching is done on trimmed exact
 * text so cosmetic whitespace differences don't drop the flag.
 */
export function isAdvisoryReason(text: string | undefined | null): boolean {
  if (!text) return false;
  return ADVISORY_REASONS.has(String(text).trim());
}

/**
 * Returns true when every reason in the list is advisory (or the list
 * is empty). Used to decide whether a failed-component entry should
 * downgrade to "advisory" status.
 */
export function areAllReasonsAdvisory(reasons: string[] | undefined | null): boolean {
  if (!reasons || reasons.length === 0) return false;
  return reasons.every(isAdvisoryReason);
}

export const FAILURE_REASON_GROUPS: FailureReasonGroup[] = [
  {
    key: 'door_leaf',
    label: 'Door Leaf',
    reasons: [
      'The door leaf is found to be damaged which may affect its fire-resisting performance.',
      'The door leaf is bowed/warped and does not fit correctly within the frame.',
      'Unauthorised alterations to the door leaf have been identified which may compromise fire integrity.',
    ],
  },
  {
    key: 'gaps_clearances',
    label: 'Gaps & Clearances',
    reasons: [
      'The clearances around the door leaf are outside the recommended tolerances (typically 2-4mm), which may affect performance in the event of fire.',
      'Excessive gaps are present which may allow the passage of smoke and fire.',
      'Threshold gap exceeds acceptable limits.',
    ],
  },
  {
    key: 'frame',
    label: 'Frame',
    reasons: [
      'The door frame is damaged and may not provide adequate support to the fire door assembly.',
      'The frame is not securely fixed to the supporting structure.',
      'Gaps are present between the frame and surrounding wall which are not adequately fire-stopped.',
    ],
  },
  {
    key: 'hinges',
    label: 'Hinges',
    reasons: [
      'The number/type of hinges is not compliant with fire door requirements (minimum 3 fire-rated hinges recommended).',
      'Hinges are not suitable for use on a fire door.',
      "Hinges are loose/damaged which may affect the door's performance.",
    ],
  },
  {
    key: 'locks_latches',
    label: 'Locks / Latches',
    reasons: [
      'The door does not fully engage with the latch, preventing effective closure in a fire situation.',
      'The latch mechanism is defective.',
      'Ironmongery fitted is not confirmed as fire-rated and may compromise performance.',
    ],
  },
  {
    key: 'self_closer',
    label: 'Self-Closing Device',
    reasons: [
      'The door is not fitted with an effective self-closing device.',
      'The self-closing device is not functioning correctly and does not fully close the door.',
      'The door fails to fully close and latch from any open position.',
    ],
  },
  {
    key: 'seals',
    label: 'Seals',
    reasons: [
      'Intumescent seals are missing which may compromise the fire resistance of the door.',
      'Seals are damaged/incomplete and do not provide continuous protection.',
      'Smoke seals are not present where required.',
    ],
  },
  {
    key: 'glazing',
    label: 'Glazing',
    reasons: [
      'Glazing installed cannot be confirmed as fire-rated.',
      'Glazing is damaged and may fail under fire conditions.',
      'Intumescent glazing seals are missing or defective.',
    ],
  },
  {
    key: 'signage',
    label: 'Signage',
    reasons: [
      'Appropriate fire door signage is not fitted.',
      'Signage is missing or not compliant with current requirements.',
    ],
  },
  {
    key: 'fire_stopping',
    label: 'Fire Stopping',
    reasons: [
      'Fire stopping between the frame and structure is missing/inadequate.',
      'Gaps around the frame have not been appropriately sealed to maintain compartmentation.',
    ],
  },
  {
    key: 'operation',
    label: 'Operation',
    reasons: [
      'The door does not operate freely and may not close fully in normal use.',
      'The door sticks/binds during operation affecting its performance.',
    ],
  },
  {
    key: 'hold_open',
    label: 'Hold-Open Devices',
    reasons: [
      'An unauthorised hold-open device (e.g. wedge) is in use.',
      'Hold-open device is not linked to the fire alarm system and is non-compliant.',
    ],
  },
  {
    key: 'certification',
    label: 'Certification',
    reasons: [
      'Fire door certification could not be verified at the time of inspection.',
      'Identification labels or plugs are missing.',
      // Iter-108p — allows the inspector to flag a solid 44 mm door
      // that would historically have been classed as a "notional" or
      // "nominal" fire door under earlier regulations. The quote
      // engine treats this as adjustment/upgrade only — no new-door
      // line item is auto-added when this reason is picked.
      'Certification could not be confirmed; however, as a solid 44 mm door, under previous regulations this may have been deemed a notional or nominal fire door — adjustment or upgrade only may be required. Further consultation may assist.',
    ],
  },
  {
    key: 'final_summary',
    label: 'Final Summary',
    reasons: [
      'The fire door is considered non-compliant and requires remedial works to meet current fire safety standards.',
      'Multiple deficiencies identified which may significantly affect fire performance.',
    ],
  },
  // ---- Component-specific reason groups (FD Check Ltd defects template, 2026 update) ----
  { key: 'smoke_seal_test', label: 'Smoke Seal Test',
    reasons: [
      'Fails lock side',
      'Fails hinge side',
      'Fails top of door',
      // Iter-108q — smoke seal fails on test, but the seals themselves
      // are physically intact. Root cause is gaps between the leaf and
      // frame, so smoke seals should NOT be added to the auto-quote.
      // The backend `auto_quote_from_inspection` detects this exact
      // phrase (via SMOKE_SEAL_GAP_REGEX) and omits the smoke-seal
      // repair line from the generated quote.
      'The smoke seal test resulted in a fail. All smoke seals are correctly fitted and appear in good physical condition with no visible damage or deterioration. The failure is attributed to gaps present between the door leaf and door casing, which prevent the seals from forming an effective airtight barrier.',
    ] },
  { key: 'glass', label: 'Glass / Vision Panel',
    reasons: ['Glass cracked', 'Not fire rated', 'Glass showing signs of delamination'] },
  { key: 'window_beads', label: 'Window Beads',
    reasons: ['Loose beads', 'Missing beads', 'Glass loose — needs resealing'] },
  { key: 'door_to_stop_bead', label: 'Door-to-Stop Bead',
    reasons: ['Stop bead damaged', 'Stop bead loose', 'Gap to stop bead excessive'] },
  { key: 'push_plates', label: 'Push / Kick Plates',
    reasons: ['Screws missing', 'Loose plate'] },
  { key: 'dropdown_seal', label: 'Dropdown Seal',
    reasons: ['Dropdown broken — needs replacing', 'No dropdown fitted', 'Cover missing'] },
  { key: 'spy_viewer', label: 'Spy Viewer',
    reasons: ['Spy viewer not fire rated', 'Spy viewer missing', 'Spy viewer damaged'] },
  { key: 'letterbox', label: 'Letterbox',
    reasons: ['Letterbox not fire rated', 'Letterbox damaged'] },
  { key: 'hold_open_release', label: 'Hold-Open Release',
    reasons: ['Batteries need replacing', 'Device is damaged', 'Not linked to the fire alarm system'] },

  // ---------------------------------------------------------------------
  // Iter-170 — Gap remedial works. The BS 8214 gap recommender
  // (src/utils/bs8214Recommendations.ts) turns the 12-point measurement
  // grid into one of these five remedies, and the backend quote engine
  // prices them from this group. Keep the reason text EXACTLY in sync
  // with REMEDY_REASONS in /app/backend/services/quote_intelligence.py
  // — that's what makes the price match exact instead of fuzzy.
  // ---------------------------------------------------------------------
  { key: 'gap_remedials',
    label: 'Gap Remedial Works',
    reasons: [
      'Fit dropdown seal / threshold bar, adjust or replace existing seal',
      'Frame build out or adjustment',
      'Frame reset required',
      'Centre door in frame (ease & re-hang)',
      'Pack bottom hinge side',
    ] },

  // ---------------------------------------------------------------------
  // Catch-all "Additional / Custom Items" group. Lives at the end so it
  // sorts AFTER all the BS 8214-derived inspection groups, but is treated
  // exactly like any other group by the pricing library — admins can add
  // reusable line items here (e.g. "Smoke alarm install", "Travel charge")
  // and they show up in future quotes alongside the inspection-driven
  // reasons.
  // ---------------------------------------------------------------------
  { key: 'additional_items',
    label: 'Additional / Custom Items',
    reasons: [] },
];

// Heuristic mapping from a question label / key to one or more reason group keys.
// Used to pre-filter the picker so an inspector sees only the relevant reasons first.
export function suggestReasonGroupKeys(questionLabel: string): string[] {
  const q = (questionLabel || '').toLowerCase();
  const hits: string[] = [];
  // Component-specific groups take priority (more specific match wins)
  if (q.includes('smoke seal test')) hits.push('smoke_seal_test');
  if (q.includes('window beads') || q.includes('window bead')) hits.push('window_beads');
  if (q.includes('door-to-stop bead') || q.includes('door to stop bead') || q.includes('stop bead')) hits.push('door_to_stop_bead');
  if (q.includes('push plate') || q.includes('kick plate')) hits.push('push_plates');
  if (q.includes('dropdown')) hits.push('dropdown_seal');
  if (q.includes('spy viewer') || q.includes('viewer')) hits.push('spy_viewer');
  if (q.includes('letterbox') || q.includes('letter box')) hits.push('letterbox');
  if (q.includes('hold-open') || q.includes('hold open') || q.includes('release mechanism')) hits.push('hold_open_release');
  if (q.includes('glass') || q.includes('vision panel')) hits.push('glass');

  // Generic groups (kept as fallback so legacy questions still work)
  if (q.includes('hinge')) hits.push('hinges');
  if (q.includes('lock') || q.includes('latch')) hits.push('locks_latches');
  if (q.includes('seal') && hits.length === 0) hits.push('seals');
  if (q.includes('gap') || q.includes('clearance')) hits.push('gaps_clearances');
  if (q.includes('close') || q.includes('closer') || q.includes('self-closing') || q.includes('self closing')) hits.push('self_closer');
  if (q.includes('frame')) hits.push('frame');
  if (q.includes('leaf')) hits.push('door_leaf');
  if (q.includes('glaz')) hits.push('glazing');
  if (q.includes('sign')) hits.push('signage');
  if (q.includes('hold') || q.includes('wedge') || q.includes('keep open')) hits.push('hold_open');
  if (q.includes('certif') || q.includes('label') || q.includes('identification')) hits.push('certification');
  if (q.includes('fire stop') || q.includes('compartmentation')) hits.push('fire_stopping');
  if (q.includes('operate') || q.includes('operation')) hits.push('operation');
  // De-duplicate while preserving order so the most specific group renders first
  return hits.length > 0 ? Array.from(new Set(hits)) : ['door_leaf'];
}

export const findReasonGroupByKey = (key: string) =>
  FAILURE_REASON_GROUPS.find((g) => g.key === key);

// ---------------------------------------------------------------------------
// Ease & Adjustment trigger detection.
//
// Per the FD Check pricing sheet ("Revised pricing for large multidoor",
// Jun 2026), certain remedial works require the door to be lifted off
// its hinges before the work can be done — and that lift+rehang+adjust
// step is billed separately as an "Ease & Adjustment" line:
//   • Single door:  £25
//   • Double door:  £35
//
// The trigger reasons are anything in the Hinges group (because swapping
// hinges literally requires unhinging the door), anything in the Frame
// group (because frame work means the door has to come out), and any
// drop-down seal / threshold install (because those slot into the
// bottom of the leaf which is only accessible with the door off).
//
// This list is consulted when the quote builder injects the line
// automatically — see /app/frontend/app/quote/create.tsx. Admins can
// remove the line from any quote, or untick "Auto-add Ease & Adjustment"
// per quote to suppress the auto-injection entirely.
// ---------------------------------------------------------------------------
export const EASE_ADJUST_PRICE_SINGLE = 25;
export const EASE_ADJUST_PRICE_DOUBLE = 35;
export const EASE_ADJUST_COMPONENT_TAG = 'Ease & Adjustment';

const EASE_ADJUST_GROUP_KEYS = new Set(['hinges', 'frame']);

export function reasonTriggersEaseAdjust(groupKey?: string, reasonText?: string): boolean {
  if (groupKey && EASE_ADJUST_GROUP_KEYS.has(groupKey)) return true;
  const r = (reasonText || '').toLowerCase();
  if (!r) return false;
  // Drop-down seal / threshold install — door must come off.
  if ((r.includes('drop') && (r.includes('down') || r.includes('seal'))) ||
      r.includes('threshold') ||
      r.includes('dropdown') ||
      r.includes('drop-down')) {
    return true;
  }
  // Catch-all: any "frame" or "hinge" mention in a custom reason.
  if (r.includes('frame') || r.includes('hinge')) return true;
  return false;
}
